/**
 * Scroll-motion engine, ported from the design hand-off (motion.js).
 *
 *   data-reveal / data-stagger   -> fade/slide elements in the first time they scroll into view
 *   data-parallax                -> translate elements relative to their distance from the viewport centre
 *   data-scrub / data-scrub-draw -> expose scroll progress as --p and draw SVG paths with it
 *   data-draw                    -> draw SVG paths the first time they scroll into view
 *   data-count                   -> count a number up the first time it scrolls into view
 *
 * It only touches inline styles, so React re-renders leave its work in place.
 * Elements are hidden by the stylesheet (html.js-motion) until prep() runs, which
 * is why prep()/prepDraw() also flip `visibility` back on explicitly.
 */
export function createMotion(opts = {}) {
  const st = Object.assign({ intensity: 1, parallax: 1 }, opts);
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const E = 'cubic-bezier(.22,.61,.36,1)';
  const seen = new WeakSet();
  const par = new Set();
  const scrubs = new Set();
  const pending = new Set();
  let raf = 0;
  const off = () => reduce || st.intensity === 0;
  const k = () => Math.max(0.25, st.intensity || 1);
  const V = {
    up: 'translate3d(0,30px,0)',
    down: 'translate3d(0,-30px,0)',
    left: 'translate3d(-40px,0,0)',
    right: 'translate3d(40px,0,0)',
    scale: 'scale(.94)',
    fade: 'none',
  };

  function prep(el) {
    const r = { o: el.style.opacity, t: el.style.transform, delay: +el.dataset.delay || 0 };
    el.__rv = r;
    pending.add(el);
    el.style.opacity = '0';
    el.style.transform = V[el.dataset.reveal] || V.up;
    el.style.willChange = 'opacity, transform';
  }
  function show(el, instant) {
    const r = el.__rv;
    if (!r) return;
    pending.delete(el);
    const d = instant ? 0 : r.delay * k();
    el.style.transition = instant
      ? 'none'
      : `opacity ${(0.6 * k() + 0.2).toFixed(2)}s ${E} ${d}ms, transform ${(0.85 * k() + 0.25).toFixed(2)}s ${E} ${d}ms`;
    el.style.opacity = r.o || '1';
    el.style.transform = r.t || 'none';
    setTimeout(() => {
      el.style.willChange = '';
    }, 1400 + d);
  }
  function prepDraw(el) {
    const L = el.getTotalLength();
    el.__dr = { L, delay: +el.dataset.delay || 0 };
    pending.add(el);
    el.style.strokeDasharray = L;
    el.style.strokeDashoffset = L;
    el.style.visibility = 'visible';
  }
  function showDraw(el, instant) {
    pending.delete(el);
    el.style.transition = instant ? 'none' : `stroke-dashoffset ${(1.3 * k() + 0.3).toFixed(2)}s ${E} ${el.__dr.delay}ms`;
    el.style.strokeDashoffset = 0;
  }
  function runCount(el, instant) {
    pending.delete(el);
    const t = el.dataset.count;
    const m = t.match(/-?[\d,]*\.?\d+/);
    if (!m) return;
    const num = parseFloat(m[0].replace(/,/g, ''));
    const dec = (m[0].split('.')[1] || '').length;
    const pre = t.slice(0, m.index);
    const suf = t.slice(m.index + m[0].length);
    const fmt = (v) => pre + v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }) + suf;
    if (instant) {
      el.textContent = fmt(num);
      return;
    }
    const T = 1300 * k() + 300;
    const t0 = performance.now();
    const f = (now) => {
      const p = Math.min(1, (now - t0) / T);
      const e = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(num * e);
      if (p < 1) requestAnimationFrame(f);
    };
    requestAnimationFrame(f);
  }
  const fire = (el, instant) => {
    if (!pending.has(el)) return;
    io.unobserve(el);
    if (el.__dr) showDraw(el, instant);
    else if (el.__ct) runCount(el, instant);
    else show(el, instant);
  };
  const io = new IntersectionObserver(
    (es) =>
      es.forEach((e) => {
        if (e.isIntersecting) fire(e.target);
      }),
    { threshold: 0.08, rootMargin: '0px 0px -6% 0px' }
  );

  function frame() {
    raf = 0;
    const vh = window.innerHeight;
    const on = off() ? 0 : 1;
    // Geometry fallback for pending reveals: fires even where IntersectionObserver is throttled.
    if (vh > 0)
      pending.forEach((el) => {
        if (!el.isConnected) {
          pending.delete(el);
          return;
        }
        const r = el.getBoundingClientRect();
        if ((r.width || r.height) && r.bottom > 0 && r.top < vh * 0.96) fire(el);
      });
    par.forEach((el) => {
      if (!el.isConnected) {
        par.delete(el);
        return;
      }
      const r = el.getBoundingClientRect();
      const c = r.top + r.height / 2 - (el.__py || 0);
      const p = c - vh / 2;
      const y = -p * (+el.dataset.parallax || 0.2) * st.parallax * on;
      const x = -p * (+el.dataset.parallaxX || 0) * st.parallax * on;
      const rot = el.dataset.parallaxRot ? ` rotate(${(+el.dataset.parallaxRot * (p / vh) * on).toFixed(2)}deg)` : '';
      el.__py = y;
      el.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0)${rot}`;
    });
    scrubs.forEach((el) => {
      if (!el.isConnected) {
        scrubs.delete(el);
        return;
      }
      const r = el.getBoundingClientRect();
      const [a, b] = (el.dataset.scrub || '0,1').split(',').map(Number);
      let p = (vh - r.top) / (vh + r.height);
      p = Math.min(1, Math.max(0, (p - a) / (b - a || 1)));
      if (!on) p = 1;
      el.style.setProperty('--p', p.toFixed(4));
      (el.__paths || []).forEach(([q, L]) => {
        q.style.strokeDashoffset = (L * (1 - p)).toFixed(1);
      });
    });
  }
  const tick = () => {
    if (!raf) raf = requestAnimationFrame(frame);
  };

  function scan(root = document) {
    root.querySelectorAll('[data-stagger]').forEach((g) => {
      if (seen.has(g)) return;
      seen.add(g);
      const step = +g.dataset.stagger || 80;
      const base = +g.dataset.delay || 0;
      Array.from(g.children).forEach((ch, i) => {
        if (!ch.dataset.reveal) ch.dataset.reveal = g.dataset.staggerVariant || 'up';
        ch.dataset.delay = base + i * step;
      });
    });
    root.querySelectorAll('[data-reveal]').forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      if (off()) return;
      prep(el);
      io.observe(el);
    });
    root.querySelectorAll('[data-draw]').forEach((el) => {
      if (seen.has(el) || !el.getTotalLength) return;
      seen.add(el);
      if (off()) return;
      prepDraw(el);
      io.observe(el);
    });
    root.querySelectorAll('[data-count]').forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      el.__ct = true;
      if (off()) {
        runCount(el, true);
        return;
      }
      pending.add(el);
      el.textContent = el.dataset.count.replace(/\d/g, '0');
      io.observe(el);
    });
    root.querySelectorAll('[data-parallax]').forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      par.add(el);
    });
    root.querySelectorAll('[data-scrub]').forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      el.__paths = Array.from(el.querySelectorAll('[data-scrub-draw]'))
        .filter((q) => q.getTotalLength)
        .map((q) => {
          const L = q.getTotalLength();
          q.style.strokeDasharray = L;
          q.style.strokeDashoffset = L;
          q.style.visibility = 'visible';
          return [q, L];
        });
      scrubs.add(el);
    });
    tick();
    [60, 400, 1200, 3000].forEach((t) => setTimeout(tick, t));
  }
  function setOpts(o) {
    Object.assign(st, o);
    if (off()) Array.from(pending).forEach((el) => fire(el, true));
    tick();
  }
  window.addEventListener('scroll', tick, { passive: true });
  window.addEventListener('resize', tick);
  document.addEventListener('visibilitychange', tick);
  window.addEventListener('pageshow', tick);
  return {
    scan,
    setOpts,
    tick,
    destroy() {
      io.disconnect();
      window.removeEventListener('scroll', tick);
      window.removeEventListener('resize', tick);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('pageshow', tick);
      cancelAnimationFrame(raf);
    },
  };
}
